<template>
  <div class="productionTrends">
    <!-- Header Navbar -->
    <b-navbar type="dark" variant="primary" class="huddle-detail-navbar">
      <b-navbar-brand tag="div" class="d-flex align-items-center">
        <span class="navbar-title">Advanced Production Trends</span>
      </b-navbar-brand>
      <b-navbar-nav class="ml-auto align-items-center">
        <!-- Help Toggle -->
        <b-nav-form class="mr-3" v-if="!busy">
          <label class="fg-toggle-label mb-0">
            <span class="fg-toggle-text">Help</span>
            <span class="fg-toggle-switch" :class="{ 'fg-toggle-on': isGuideActive }" @click="isGuideActive = !isGuideActive">
              <span class="fg-toggle-knob"></span>
            </span>
          </label>
        </b-nav-form>
        <b-nav-item @click="closeSlideOutPanel">
          <i class="la la-close fs-lg" />
        </b-nav-item>
      </b-navbar-nav>
    </b-navbar>

    <!-- Loading Skeleton -->
    <ProductionTrendsSkeleton v-if="busy" />

      <b-container fluid class="py-3" v-show="!busy" v-if="!busy || hasData">

        <!-- Filters Row -->
        <b-row class="mb-3">
          <!-- Date Range (Period A) -->
          <b-col lg="5" md="12" class="mb-2">
            <label class="fw-semi-bold fs-sm mb-1">
              {{ compareMode ? 'Period A' : 'Date Range' }}
            </label>
            <div id="pt-date-presets" class="timeframe-buttons mb-2" v-if="!compareMode">
              <a href="#" v-for="preset in datePresets" :key="preset.key"
                @click.prevent="setRange(preset.key)"
                class="time-btn" :class="{ active: activePreset === preset.key }">
                {{ preset.label }}
              </a>
            </div>
            <div class="timeframe-buttons mb-2" v-if="compareMode">
              <a href="#" v-for="preset in comparisonPresets" :key="preset.key"
                @click.prevent="setComparisonPreset(preset.key)"
                class="time-btn" :class="{ active: comparisonPreset === preset.key }">
                {{ preset.label }}
              </a>
            </div>
            <DatePicker lang="en" placeholder="Select Date Range" range
              v-model="selectedTimeFrame" format="YYYY-MM-DD"
              type="date" class="input-sm w-100" input-class="form-control"
              @change="onDateChange" />
            <!-- Period B Date Picker -->
            <div v-if="compareMode" class="mt-2">
              <label class="fw-semi-bold fs-sm mb-1">Period B</label>
              <DatePicker lang="en" placeholder="Select Period B Range" range
                v-model="comparisonTimeFrame" format="YYYY-MM-DD"
                type="date" class="input-sm w-100" input-class="form-control"
                @change="onPeriodBDateChange" />
            </div>
          </b-col>

          <!-- Provider Multi-select -->
          <b-col id="pt-provider-select" lg="4" md="8" class="mb-2">
            <label class="fw-semi-bold fs-sm mb-1">Providers</label>
            <multiselect v-model="selectedProviders" :options="availableProviders"
              :multiple="true" :taggable="false" :show-labels="false"
              :custom-label="providerLabel" track-by="ProvNum"
              placeholder="Select Providers" :close-on-select="false">
            </multiselect>
          </b-col>

          <!-- Y-Axis Toggle + Compare Toggle -->
          <b-col lg="3" md="4" class="mb-2">
            <label class="fw-semi-bold fs-sm mb-1">Display Mode</label>
            <div id="pt-display-mode" class="d-flex mb-2">
              <b-button :variant="yAxisMode === 'perHour' ? 'primary' : 'outline-secondary'"
                @click="yAxisMode = 'perHour'" size="sm" class="flex-fill mr-1">
                $/hr
              </b-button>
              <b-button :variant="yAxisMode === 'totalNet' ? 'primary' : 'outline-secondary'"
                @click="yAxisMode = 'totalNet'" size="sm" class="flex-fill">
                Total Net $
              </b-button>
            </div>
            <b-button id="pt-compare-btn" :variant="compareMode ? 'warning' : 'outline-secondary'"
              @click="toggleCompareMode" size="sm" block>
              <i class="la la-columns mr-1" />
              {{ compareMode ? 'Exit Compare' : 'Compare Periods' }}
            </b-button>
          </b-col>
        </b-row>

        <!-- View Selector -->
        <b-row class="mb-3">
          <b-col>
            <div id="pt-view-selector" class="d-flex">
              <b-button :variant="activeView === 'chart' ? 'primary' : 'outline-secondary'"
                @click="activeView = 'chart'" size="sm" class="mr-1">
                <i class="la la-bar-chart mr-1" /> Bar Chart
              </b-button>
              <b-button :variant="activeView === 'rankings' ? 'primary' : 'outline-secondary'"
                @click="activeView = 'rankings'" size="sm">
                <i class="la la-trophy mr-1" /> Rankings
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- Bar Chart View -->
        <b-row class="mb-3" v-show="activeView === 'chart'">
          <b-col>
            <div class="border-box p-3 css-chart-wrapper" ref="chartWrapper"
              v-if="chartData && chartData.datasets && chartData.datasets.length > 0">
              <!-- Chart Title -->
              <div class="css-chart-title">
                {{ yAxisMode === 'perHour' ? 'Production Per Hour ($/hr)' : 'Total Net Production ($)' }}
              </div>

              <div id="pt-chart" class="css-chart" @mouseleave="hoveredBar = null">
                <!-- Y-Axis Labels (aligned to plot area only) -->
                <div class="css-chart-yaxis">
                  <div class="css-chart-yaxis-plot">
                    <span v-for="(tick, ti) in yAxisTicks" :key="ti"
                      class="css-chart-ytick"
                      :style="{ bottom: (tick / chartMaxValue * 100) + '%' }">
                      {{ formatTickValue(tick) }}
                    </span>
                  </div>
                  <div class="css-chart-yaxis-xlabel-spacer"></div>
                </div>

                <!-- Chart Content -->
                <div class="css-chart-area">
                  <!-- Plot area: gridlines + bars share same container -->
                  <div class="css-chart-plot">
                    <!-- Grid lines -->
                    <div v-for="(tick, ti) in yAxisTicks" :key="'g' + ti"
                      class="css-chart-gridline"
                      :style="{ bottom: (tick / chartMaxValue * 100) + '%' }">
                    </div>

                    <!-- Bar Groups (same height as plot) -->
                    <div class="css-chart-groups">
                      <div v-for="(label, li) in chartData.labels" :key="li" class="css-chart-group">
                        <div v-for="(ds, di) in chartData.datasets" :key="di"
                          class="css-chart-bar"
                          :style="{
                            height: (chartMaxValue > 0 ? Math.max((ds.data[li] || 0) / chartMaxValue * 100, 0) : 0) + '%',
                            backgroundColor: ds.backgroundColor,
                            borderColor: ds.borderColor || 'transparent',
                            borderWidth: ds.borderColor ? '1px' : '0',
                            borderStyle: ds.borderColor ? 'solid' : 'none',
                          }"
                          @mouseenter="onBarHover($event, li, di, label, ds.label, ds.data[li])"
                        >
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- X-Axis Labels (separate row, outside plot area) -->
                  <div class="css-chart-xlabels">
                    <div v-for="(label, li) in chartData.labels" :key="'x' + li"
                      class="css-chart-xlabel">
                      {{ label }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Floating Tooltip (rendered above everything, not clipped) -->
              <div class="css-chart-bar-tooltip" v-if="hoveredBar"
                :style="{ top: hoveredBar.top + 'px', left: hoveredBar.left + 'px' }">
                <strong>{{ hoveredBar.dsLabel }}</strong><br>
                {{ formatBarValue(hoveredBar.value) }}
              </div>

              <!-- Legend -->
              <div class="css-chart-legend" v-if="selectedProviders.length > 1 || compareMode">
                <span v-for="(ds, i) in chartData.datasets" :key="i" class="css-chart-legend-item">
                  <span class="css-chart-legend-swatch" :style="{ backgroundColor: ds.backgroundColor }"></span>
                  {{ ds.label }}
                </span>
              </div>
            </div>

            <div v-if="!busy && (!chartData || !chartData.datasets || chartData.datasets.length === 0)"
              class="text-center text-muted py-5">
              <i class="la la-bar-chart" style="font-size: 3rem;" />
              <p class="mt-2">No production data found for the selected filters.</p>
            </div>
          </b-col>
        </b-row>

        <!-- Rankings View -->
        <b-row class="mb-3" v-if="activeView === 'rankings'">
          <b-col>
            <div class="border-box p-3" v-if="rankingsData">
              <div class="d-flex align-items-center mb-3">
                <h6 class="fw-semi-bold mb-0 mr-3">Provider Rankings</h6>
                <div class="d-flex">
                  <b-button :variant="rankingMetric === 'perHour' ? 'primary' : 'outline-secondary'"
                    @click="rankingMetric = 'perHour'" size="sm" class="mr-1">$/hr</b-button>
                  <b-button :variant="rankingMetric === 'totalNet' ? 'primary' : 'outline-secondary'"
                    @click="rankingMetric = 'totalNet'" size="sm" class="mr-1">Net Production</b-button>
                  <b-button :variant="rankingMetric === 'clinicalHours' ? 'primary' : 'outline-secondary'"
                    @click="rankingMetric = 'clinicalHours'" size="sm">Clinical Hours</b-button>
                </div>
              </div>

              <div class="rankings-list">
                <div v-for="prov in rankingsData.providers" :key="prov.provNum" class="ranking-row">
                  <div class="ranking-rank">#{{ prov.rank }}</div>
                  <div class="ranking-info">
                    <div class="ranking-name fw-semi-bold">{{ prov.provider }}</div>
                    <div class="ranking-bar-wrapper">
                      <div class="ranking-bar" :style="{ width: prov.barWidth + '%', backgroundColor: prov.color }"></div>
                    </div>
                  </div>
                  <div class="ranking-values">
                    <div class="ranking-primary fw-bold">
                      <template v-if="rankingMetric === 'perHour'">{{ prov.perHour !== null ? $options.filters.money(prov.perHour, currencySymbol) + '/hr' : 'N/A' }}</template>
                      <template v-else-if="rankingMetric === 'totalNet'">{{ $options.filters.money(prov.totalNet, currencySymbol) }}</template>
                      <template v-else>{{ prov.totalClinicalHours.toFixed(1) }} hrs</template>
                    </div>
                    <div class="ranking-secondary text-muted fs-sm">
                      <template v-if="rankingMetric === 'perHour'">{{ prov.totalClinicalHours.toFixed(1) }} hrs</template>
                      <template v-else-if="rankingMetric === 'totalNet'">{{ prov.workingDays }} days</template>
                      <template v-else>{{ $options.filters.money(prov.totalNet, currencySymbol) }}</template>
                    </div>
                  </div>
                </div>

                <!-- Practice Average Reference -->
                <div class="ranking-row ranking-avg-row">
                  <div class="ranking-rank">&mdash;</div>
                  <div class="ranking-info">
                    <div class="ranking-name text-muted">Practice Average</div>
                    <div class="ranking-bar-wrapper">
                      <div class="ranking-avg-line"
                        :style="{ left: (rankingsData.maxValue > 0 ? (rankingsData.practiceAvgValue / rankingsData.maxValue) * 100 : 0) + '%' }"></div>
                    </div>
                  </div>
                  <div class="ranking-values">
                    <div class="ranking-primary fw-bold text-muted">
                      <template v-if="rankingMetric === 'perHour'">{{ $options.filters.money(rankingsData.practiceAvgValue, currencySymbol) }}/hr</template>
                      <template v-else-if="rankingMetric === 'totalNet'">{{ $options.filters.money(rankingsData.practiceAvgValue, currencySymbol) }}</template>
                      <template v-else>{{ rankingsData.practiceAvgValue.toFixed(1) }} hrs</template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-muted py-5">
              <i class="la la-trophy" style="font-size: 3rem;" />
              <p class="mt-2">No data available for rankings.</p>
            </div>
          </b-col>
        </b-row>

        <!-- Summary Metrics -->
        <b-row id="pt-summary-metrics" class="mb-3" v-if="summaryMetrics">
          <b-col sm="6" md="3" v-for="(metric, idx) in summaryMetrics" :key="idx" class="mb-2">
            <div class="border-box p-3 text-center">
              <div class="h4 fw-bold mb-1">{{ metric.value }}</div>
              <div class="text-muted fs-sm" :title="metric.hint">{{ metric.label }}</div>
              <div v-if="metric.trend" class="trend-indicator mt-1"
                :class="metric.trend.direction === 'up' ? 'trend-up' : 'trend-down'">
                {{ metric.trend.direction === 'up' ? '\u2191' : '\u2193' }}
                {{ metric.trend.percentage }}% {{ metric.trend.label }}
              </div>
              <div v-else-if="comparisonResults" class="trend-indicator trend-neutral mt-1">
                &mdash; no prior data
              </div>
            </div>
          </b-col>
        </b-row>

        <!-- Smart Insights -->
        <SmartInsights
          v-if="smartInsightsMetrics"
          :metrics="smartInsightsMetrics"
          context-label="Production"
          class="mb-3"
        />

        <!-- Data Table -->
        <b-row id="pt-data-table" v-if="sortedTableData && sortedTableData.length > 0">
          <b-col>
            <div class="table-responsive border-box">
              <table class="table table-striped mb-0">
                <thead>
                  <tr>
                    <th v-for="col in tableColumns" :key="col.key"
                      @click="sortTable(col.key)" style="cursor:pointer; user-select:none;">
                      {{ col.label }}
                      <i v-if="sortBy === col.key"
                        :class="sortDirection === 'asc' ? 'la la-sort-up' : 'la la-sort-down'" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in sortedTableData" :key="idx">
                    <td v-if="compareMode">{{ row.period }}</td>
                    <td>{{ row.dateLabel }}</td>
                    <td>{{ row.provider }}</td>
                    <td>{{ row.grossProduction | money(currencySymbol) }}</td>
                    <td>{{ row.writeoffs | money(currencySymbol) }}</td>
                    <td>{{ row.adjustments | money(currencySymbol) }}</td>
                    <td class="fw-semi-bold">{{ row.netProduction | money(currencySymbol) }}</td>
                    <td :title="hoursSourceLabel(row.hoursSource)">{{ row.clinicalHours > 0 ? row.clinicalHours.toFixed(2) + ' hrs' : '-' }}</td>
                    <td class="fw-semi-bold">
                      {{ row.perHour !== null ? $options.filters.money(row.perHour, currencySymbol) + '/hr' : '-' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-col>
        </b-row>

      </b-container>

    <!-- Feature Guide -->
    <FeatureGuide
      :steps="guideSteps"
      mode="helpmode"
      feature-key="production-trends"
      :active="isGuideActive"
      @close="isGuideActive = false"
      @complete="isGuideActive = false"
    />
  </div>
</template>

<script>
/* eslint-disable */

import Vue from 'vue';
import mixin from '../../../mixin.js';
import { mapState, mapActions } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import DatePicker from 'vue2-datepicker';
import Multiselect from 'vue-multiselect';
import FeatureGuide from '@/components/FeatureGuide/FeatureGuide.vue';
import SmartInsights from '@/components/SmartInsights/SmartInsights.vue';
import ProductionTrendsSkeleton from './ProductionTrendsSkeleton.vue';
import {
  groupAppointmentsByProviderDay,
  groupScheduleByProviderDay,
  resolveLunchMinutes,
  computeDayHours,
} from '@/utils/huddle/clinicalHours';

// Hover text for the "Total Clinical Hours" tiles - same wording idea as the production-per-hour glossary entry.
const CLINICAL_HOURS_HINT = 'Paid chair time: from the earlier of the schedule start and the first appointment to the later of the schedule end and the end of the last appointment. Lunch from Set Goals (or the empty gap in a split schedule, whichever is larger) is subtracted; no lunch is taken off for a provider without one set. Broken appointments and gaps still count.';

const PROVIDER_COLORS = [
  '#3abf94', // brand green
  '#5b8ff9', // blue
  '#f6bd16', // gold
  '#e86452', // salmon
  '#6dc8ec', // light blue
  '#945fb9', // purple
  '#ff9845', // orange
  '#1e9493', // teal
  '#ff99c3', // pink
  '#269a99', // dark teal
];

export default {
  name: 'ProductionTrends',
  props: ['d', 'huddleGoals'],
  mixins: [mixin],
  components: { DatePicker, Multiselect, FeatureGuide, SmartInsights, ProductionTrendsSkeleton },

  data() {
    return {
      busy: false,
      results: null,
      comparisonResults: null,
      hoveredBar: null,
      selectedTimeFrame: [
        new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        new Date()
      ],
      activePreset: 'thismonth',
      selectedProviders: [],
      yAxisMode: 'perHour',
      activeView: 'chart',
      rankingMetric: 'perHour',
      compareMode: false,
      comparisonTimeFrame: [null, null],
      comparisonPreset: null,
      periodBResults: null,
      sortBy: 'dateLabel',
      sortDirection: 'asc',
      // Feature Guide
      isGuideActive: false,
      guideSteps: [
        {
          target: '#pt-date-presets',
          title: 'Date Range Presets',
          content: 'Pick a time period to analyze. "This Week" shows daily production so you can catch a slow week early. "This Month" or "This Year" lets you see monthly trends to track growth over time. You can also pick a custom range using the date picker below.',
          placement: 'bottom',
        },
        {
          target: '#pt-provider-select',
          title: 'Provider Selection',
          content: 'Select one or more providers (dentists or hygienists) to see their production side by side in the chart. This lets you compare how each provider is performing during the selected time period.',
          placement: 'bottom',
        },
        {
          target: '#pt-display-mode',
          title: 'Display Mode: $/hr vs Total Net $',
          content: '$/hr shows how efficiently each provider produces per clinical hour worked. Total Net $ shows the raw dollar amount. Net Production is calculated as: Gross Production minus Insurance Writeoffs plus Adjustments. Clinical hours run from the earlier of the Open Dental schedule start and the first appointment to the later of the schedule end and the end of the last appointment (overlapping schedule entries are counted once), minus the lunch set in Set Goals or the gap in a split schedule, whichever is larger. A provider with no lunch set has nothing subtracted. Hygienists are counted from the appointment Hygienist field.',
          placement: 'left',
        },
        {
          target: '#pt-compare-btn',
          title: 'Compare Two Time Periods',
          content: 'Turn this on to compare two time periods side by side — for example, this month vs last month, or this year vs last year. This helps you see if your practice production is growing or declining over time.',
          placement: 'left',
        },
        {
          target: '#pt-view-selector',
          title: 'Bar Chart vs Rankings',
          content: 'Bar Chart shows production trends over time (days or months). Rankings shows a provider leaderboard sorted by $/hr, net production, or clinical hours — with a practice average reference line so you can see who is above or below average.',
          placement: 'bottom',
        },
        {
          target: '#pt-chart',
          title: 'Production Chart',
          content: 'Each bar represents a provider\'s production for a specific day or month. When multiple providers are selected, their bars appear side by side for easy comparison. Hover over any bar for detailed numbers.',
          placement: 'top',
        },
        {
          target: '#pt-summary-metrics',
          title: 'Summary Metrics & Trends',
          content: 'Your key performance numbers at a glance: total production, average $/hr, clinical hours, and working days. The trend arrows compare this period to the previous one — green up means improvement, red down means decline.',
          placement: 'top',
        },
        {
          target: '#pt-data-table',
          title: 'Detailed Data Table',
          content: 'The full breakdown of your production data in table form. Click any column header to sort. This shows the exact numbers behind the chart — useful for finding specific days or providers.',
          placement: 'top',
        },
      ],
      datePresets: [
        { key: 'thisweek', label: 'This Week' },
        { key: 'lastweek', label: 'Last Week' },
        { key: 'thismonth', label: 'This Month' },
        { key: 'lastmonth', label: 'Last Month' },
        { key: 'thisyear', label: 'This Year' },
        { key: 'lastyear', label: 'Last Year' },
      ],
      comparisonPresets: [
        { key: 'monthVsMonth', label: 'This Month vs Last Month' },
        { key: 'weekVsWeek', label: 'This Week vs Last Week' },
        { key: 'yearVsYear', label: 'This Year vs Last Year' },
      ],
    };
  },

  created() {
    this.init();
  },

  watch: {
    compareMode(val) {
      if (val) {
        this.setComparisonPreset('monthVsMonth');
      } else {
        this.periodBResults = null;
        this.comparisonPreset = null;
        this.fetchComparisonData();
      }
    },
  },

  computed: {
    currencySymbol() {
      return this.allSettings?.GENERAL_CURRENCY_SYMBOL?.PropertyValue || '$';
    },

    hasData() {
      return this.results && this.results.data && this.results.data.length > 0;
    },

    // Raw result sets from API (indices match production-trends.js query order)
    grossProductionRaw() {
      return this.results?.data?.[0] || [];
    },
    writeoffRaw() {
      return this.results?.data?.[1] || [];
    },
    adjustmentsRaw() {
      return this.results?.data?.[2] || [];
    },
    clinicalHoursAppointmentsRaw() {
      return this.results?.data?.[3] || [];
    },
    // Open Dental provider schedule blocks (index 4). Guarded: an older backend returns only 4 result sets,
    // in which case clinical hours run from the first appointment to the end of the latest one.
    providerScheduleRaw() {
      const rows = this.results?.data?.[4];
      return Array.isArray(rows) ? rows : [];
    },

    // Unique providers extracted from gross production results
    availableProviders() {
      if (!this.grossProductionRaw || this.grossProductionRaw.length === 0) return [];
      const provMap = {};
      this.grossProductionRaw.forEach(row => {
        if (row.ProvNum && !provMap[row.ProvNum]) {
          provMap[row.ProvNum] = {
            ProvNum: row.ProvNum,
            Provider: row.Provider,
            IsSecondary: row.IsSecondary,
          };
        }
      });
      return Object.values(provMap).sort((a, b) => a.Provider.localeCompare(b.Provider));
    },

    // Date range span in days
    dateRangeSpan() {
      if (!this.selectedTimeFrame || !this.selectedTimeFrame[0] || !this.selectedTimeFrame[1]) return 0;
      return moment(this.selectedTimeFrame[1]).diff(moment(this.selectedTimeFrame[0]), 'days') + 1;
    },

    // Whether to aggregate by month (>31 days span in either period)
    isMonthlyView() {
      if (this.compareMode && this.comparisonTimeFrame && this.comparisonTimeFrame[0] && this.comparisonTimeFrame[1]) {
        const periodBSpan = moment(this.comparisonTimeFrame[1]).diff(moment(this.comparisonTimeFrame[0]), 'days') + 1;
        return this.dateRangeSpan > 31 || periodBSpan > 31;
      }
      return this.dateRangeSpan > 31;
    },

    // Comparison period date range (auto-determined from current selection)
    comparisonDateRange() {
      if (!this.selectedTimeFrame || !this.selectedTimeFrame[0] || !this.selectedTimeFrame[1]) return null;

      const from = moment(this.selectedTimeFrame[0]);
      const to = moment(this.selectedTimeFrame[1]);

      switch(this.activePreset) {
        case 'thisweek':
          return {
            dateFrom: moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
            dateTo: moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD'),
          };
        case 'lastweek':
          return {
            dateFrom: moment().subtract(2, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
            dateTo: moment().subtract(2, 'weeks').endOf('isoWeek').format('YYYY-MM-DD'),
          };
        case 'thismonth':
          return {
            dateFrom: moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
            dateTo: moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'),
          };
        case 'lastmonth':
          return {
            dateFrom: moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
            dateTo: moment().subtract(2, 'months').endOf('month').format('YYYY-MM-DD'),
          };
        case 'thisyear':
          return {
            dateFrom: moment().subtract(1, 'years').startOf('year').format('YYYY-MM-DD'),
            dateTo: moment().subtract(1, 'years').endOf('year').format('YYYY-MM-DD'),
          };
        case 'lastyear':
          return {
            dateFrom: moment().subtract(2, 'years').startOf('year').format('YYYY-MM-DD'),
            dateTo: moment().subtract(2, 'years').endOf('year').format('YYYY-MM-DD'),
          };
        default: {
          const span = to.diff(from, 'days');
          return {
            dateFrom: moment(from).subtract(span + 1, 'days').format('YYYY-MM-DD'),
            dateTo: moment(from).subtract(1, 'days').format('YYYY-MM-DD'),
          };
        }
      }
    },

    comparisonPeriodLabel() {
      switch(this.activePreset) {
        case 'thisweek': return 'vs last week';
        case 'lastweek': return 'vs prior week';
        case 'thismonth': return 'vs last month';
        case 'lastmonth': return 'vs prior month';
        case 'thisyear': return 'vs last year';
        case 'lastyear': return 'vs prior year';
        default: return 'vs prior period';
      }
    },

    comparisonSummary() {
      return this.computeSummaryFromResults(this.comparisonResults);
    },

    // Process raw data into daily per-provider metrics (delegates to reusable method)
    dailyProviderData() {
      return this._buildDailyData(this.results);
    },

    // Period B daily data (comparison mode only)
    periodBDailyProviderData() {
      if (!this.compareMode || !this.periodBResults) return {};
      return this._buildDailyData(this.periodBResults);
    },

    // Aggregated data for chart display (daily or monthly depending on span)
    aggregatedData() {
      return this._aggregateData(this.dailyProviderData, this.isMonthlyView);
    },

    // Period B aggregated data (comparison mode only)
    periodBAggregatedData() {
      return this._aggregateData(this.periodBDailyProviderData, this.isMonthlyView);
    },

    // Selected provider ProvNums as strings for matching
    selectedProvNums() {
      return this.selectedProviders.map(p => String(p.ProvNum));
    },

    // Chart data (handles both single-period and comparison modes)
    chartData() {
      if (this.compareMode) return this._buildComparisonChartData();

      const aggregated = this.aggregatedData;
      if (!aggregated || Object.keys(aggregated).length === 0 || this.selectedProviders.length === 0) {
        return { labels: [], datasets: [] };
      }

      const sortedKeys = Object.keys(aggregated).sort().filter(key => {
        return this.selectedProvNums.some(provNum => aggregated[key] && aggregated[key][provNum]);
      });

      if (sortedKeys.length === 0) {
        return { labels: [], datasets: [] };
      }

      const labels = sortedKeys.map(key => {
        if (this.isMonthlyView) {
          return moment(key, 'YYYY-MM').format('MMM YYYY');
        }
        return moment(key).format('MMM D');
      });

      const datasets = [];
      this.selectedProviders.forEach((prov, index) => {
        const provNum = String(prov.ProvNum);
        const color = PROVIDER_COLORS[index % PROVIDER_COLORS.length];

        const chartValues = sortedKeys.map(key => {
          const dateData = aggregated[key];
          if (!dateData || !dateData[provNum]) return 0;

          if (this.yAxisMode === 'perHour') {
            return dateData[provNum].perHour !== null ? parseFloat(dateData[provNum].perHour.toFixed(2)) : 0;
          }
          return parseFloat(dateData[provNum].netProduction.toFixed(2));
        });

        datasets.push({
          label: prov.Provider,
          data: chartValues,
          backgroundColor: color,
          borderWidth: 1,
        });
      });

      return { labels, datasets };
    },

    // Max value across all chart datasets (for Y-axis scaling)
    chartMaxValue() {
      if (!this.chartData || !this.chartData.datasets || this.chartData.datasets.length === 0) return 100;
      let max = 0;
      this.chartData.datasets.forEach(ds => {
        ds.data.forEach(v => { if (v > max) max = v; });
      });
      if (max === 0) return 100;
      // Round up to a nice number
      const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
      const niceMax = Math.ceil(max / magnitude) * magnitude;
      // If niceMax is too close to max, bump up
      return niceMax <= max ? niceMax + magnitude : niceMax;
    },

    // Y-axis tick values (5 evenly spaced from 0 to chartMaxValue)
    yAxisTicks() {
      const max = this.chartMaxValue;
      if (max <= 0) return [0];
      const step = max / 4;
      return [0, step, step * 2, step * 3, max];
    },

    // Summary metrics for the cards section (with trend indicators from comparison period)
    summaryMetrics() {
      if (!this.hasData || this.selectedProviders.length === 0) return null;

      const daily = this.dailyProviderData;
      let totalNet = 0;
      let totalClinicalHours = 0;
      let workingDays = new Set();

      Object.keys(daily).forEach(dateStr => {
        this.selectedProvNums.forEach(provNum => {
          if (daily[dateStr] && daily[dateStr][provNum]) {
            const entry = daily[dateStr][provNum];
            totalNet += entry.netProduction;
            totalClinicalHours += entry.clinicalHours;
            if (entry.netProduction !== 0 || entry.clinicalHours > 0) {
              workingDays.add(dateStr);
            }
          }
        });
      });

      const avgPerHour = totalClinicalHours > 0 ? totalNet / totalClinicalHours : null;
      const dailyAvg = workingDays.size > 0 ? totalNet / workingDays.size : 0;

      // Trend calculation helper (comparison mode uses Period B; default uses auto-comparison)
      const comp = this.compareMode
        ? this.computeSummaryFromResults(this.periodBResults)
        : this.comparisonSummary;
      const periodLabel = this.compareMode ? 'vs Period B' : this.comparisonPeriodLabel;
      const getTrend = (current, previous) => {
        if (comp === null || previous === null || previous === undefined || previous === 0) return null;
        const pct = ((current - previous) / Math.abs(previous)) * 100;
        return {
          direction: pct >= 0 ? 'up' : 'down',
          percentage: Math.abs(pct).toFixed(1),
          label: periodLabel,
        };
      };

      const metrics = [];

      metrics.push({
        label: 'Total Net Production',
        value: this.$options.filters.money(totalNet, this.currencySymbol),
        trend: getTrend(totalNet, comp?.totalNet),
      });

      if (this.yAxisMode === 'perHour') {
        metrics.push({
          label: 'Average $/hr',
          value: avgPerHour !== null ? this.$options.filters.money(avgPerHour, this.currencySymbol) + '/hr' : 'N/A',
          trend: getTrend(avgPerHour, comp?.avgPerHour),
        });
        metrics.push({
          label: 'Total Clinical Hours',
          hint: CLINICAL_HOURS_HINT,
          value: totalClinicalHours.toFixed(1) + ' hrs',
          trend: getTrend(totalClinicalHours, comp?.totalClinicalHours),
        });
      } else {
        metrics.push({
          label: 'Daily Average',
          value: this.$options.filters.money(dailyAvg, this.currencySymbol),
          trend: getTrend(dailyAvg, comp?.dailyAvg),
        });
        metrics.push({
          label: 'Total Clinical Hours',
          hint: CLINICAL_HOURS_HINT,
          value: totalClinicalHours.toFixed(1) + ' hrs',
          trend: getTrend(totalClinicalHours, comp?.totalClinicalHours),
        });
      }

      metrics.push({
        label: 'Working Days',
        value: workingDays.size.toString(),
        trend: getTrend(workingDays.size, comp?.workingDays),
      });

      return metrics;
    },

    // Smart Insights metrics (standardized shape for SmartInsights component)
    smartInsightsMetrics() {
      if (!this.summaryMetrics) return null;

      const daily = this.dailyProviderData;
      let totalNet = 0, totalClinicalHours = 0, workingDays = new Set();
      Object.keys(daily).forEach(dateStr => {
        this.selectedProvNums.forEach(provNum => {
          if (daily[dateStr] && daily[dateStr][provNum]) {
            const entry = daily[dateStr][provNum];
            totalNet += entry.netProduction;
            totalClinicalHours += entry.clinicalHours;
            if (entry.netProduction !== 0 || entry.clinicalHours > 0) workingDays.add(dateStr);
          }
        });
      });

      const current = {
        totalNet,
        totalClinicalHours,
        avgPerHour: totalClinicalHours > 0 ? totalNet / totalClinicalHours : null,
        dailyAvg: workingDays.size > 0 ? totalNet / workingDays.size : 0,
        workingDays: workingDays.size,
      };

      const comp = this.compareMode
        ? this.computeSummaryFromResults(this.periodBResults)
        : this.comparisonSummary;

      const provData = this.rankingsData && this.rankingsData.providers
        ? this.rankingsData.providers.map(p => ({
            name: p.provider,
            totalNet: p.totalNet,
            clinicalHours: p.totalClinicalHours,
            perHour: p.perHour,
            workingDays: p.workingDays,
          }))
        : null;

      return {
        current,
        prior: comp || null,
        providers: provData && provData.length > 1 ? provData : null,
        dateRangeSpan: this.dateRangeSpan,
        activePreset: this.activePreset,
        currencySymbol: this.currencySymbol,
      };
    },

    // Table column definitions
    tableColumns() {
      const cols = [];
      if (this.compareMode) cols.push({ key: 'period', label: 'Period' });
      cols.push(
        { key: 'dateLabel', label: 'Date' },
        { key: 'provider', label: 'Provider' },
        { key: 'grossProduction', label: 'Gross' },
        { key: 'writeoffs', label: 'Writeoffs' },
        { key: 'adjustments', label: 'Adjustments' },
        { key: 'netProduction', label: 'Net Production' },
        { key: 'clinicalHours', label: 'Clinical Hrs' },
        { key: 'perHour', label: '$/hr' },
      );
      return cols;
    },

    // Flat table data from aggregated data (includes Period B rows in comparison mode)
    tableData() {
      const rows = [];

      // Period A rows
      const aggregated = this.aggregatedData;
      if (aggregated && Object.keys(aggregated).length > 0) {
        const sortedKeys = Object.keys(aggregated).sort();
        sortedKeys.forEach(key => {
          this.selectedProvNums.forEach(provNum => {
            if (aggregated[key] && aggregated[key][provNum]) {
              const entry = aggregated[key][provNum];
              rows.push({
                period: this.compareMode ? 'Period A' : undefined,
                dateKey: key,
                dateLabel: this.isMonthlyView ? moment(key, 'YYYY-MM').format('MMM YYYY') : moment(key).format('MMM D, YYYY'),
                provider: entry.providerName,
                provNum,
                grossProduction: entry.gross,
                writeoffs: entry.writeoffs,
                adjustments: entry.adjustments,
                netProduction: entry.netProduction,
                clinicalHours: entry.clinicalHours,
                hoursSource: entry.hoursSource,
                perHour: entry.perHour,
              });
            }
          });
        });
      }

      // Period B rows (comparison mode)
      if (this.compareMode) {
        const aggB = this.periodBAggregatedData;
        if (aggB && Object.keys(aggB).length > 0) {
          const sortedKeys = Object.keys(aggB).sort();
          sortedKeys.forEach(key => {
            this.selectedProvNums.forEach(provNum => {
              if (aggB[key] && aggB[key][provNum]) {
                const entry = aggB[key][provNum];
                rows.push({
                  period: 'Period B',
                  dateKey: key,
                  dateLabel: this.isMonthlyView ? moment(key, 'YYYY-MM').format('MMM YYYY') : moment(key).format('MMM D, YYYY'),
                  provider: entry.providerName,
                  provNum,
                  grossProduction: entry.gross,
                  writeoffs: entry.writeoffs,
                  adjustments: entry.adjustments,
                  netProduction: entry.netProduction,
                  clinicalHours: entry.clinicalHours,
                  hoursSource: entry.hoursSource,
                  perHour: entry.perHour,
                });
              }
            });
          });
        }
      }

      return rows;
    },

    // Sorted table data
    sortedTableData() {
      if (!this.tableData || this.tableData.length === 0) return [];

      const sorted = [...this.tableData];
      sorted.sort((a, b) => {
        let aVal = a[this.sortBy];
        let bVal = b[this.sortBy];

        // Use dateKey for proper chronological sorting
        if (this.sortBy === 'dateLabel') {
          aVal = a.dateKey;
          bVal = b.dateKey;
        }

        if (aVal === null || aVal === undefined) aVal = -Infinity;
        if (bVal === null || bVal === undefined) bVal = -Infinity;

        if (typeof aVal === 'string') {
          return this.sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      });

      return sorted;
    },

    // Provider rankings: aggregate ALL providers across all dates, sorted by metric
    rankingsData() {
      const daily = this.dailyProviderData;
      if (!daily || Object.keys(daily).length === 0) return null;

      const providerAgg = {};

      Object.keys(daily).forEach(dateStr => {
        Object.keys(daily[dateStr]).forEach(provNum => {
          const entry = daily[dateStr][provNum];
          if (!providerAgg[provNum]) {
            providerAgg[provNum] = {
              provNum, provider: entry.providerName,
              totalNet: 0, totalClinicalHours: 0, workingDays: 0,
            };
          }
          providerAgg[provNum].totalNet += entry.netProduction;
          providerAgg[provNum].totalClinicalHours += entry.clinicalHours;
          if (entry.netProduction !== 0 || entry.clinicalHours > 0) {
            providerAgg[provNum].workingDays++;
          }
        });
      });

      const getValue = (p) => {
        if (this.rankingMetric === 'perHour') return p.perHour || 0;
        if (this.rankingMetric === 'totalNet') return p.totalNet;
        return p.totalClinicalHours;
      };

      const providers = Object.values(providerAgg).map(p => {
        p.perHour = p.totalClinicalHours > 0 ? p.totalNet / p.totalClinicalHours : null;
        p.dailyAvg = p.workingDays > 0 ? p.totalNet / p.workingDays : 0;
        return p;
      }).filter(p => p.perHour !== null || p.totalNet !== 0);

      if (providers.length === 0) return null;

      providers.sort((a, b) => getValue(b) - getValue(a));

      const practiceAvgValue = providers.reduce((sum, p) => sum + getValue(p), 0) / providers.length;
      const maxValue = providers.length > 0 ? getValue(providers[0]) : 1;

      providers.forEach((p, idx) => {
        p.rank = idx + 1;
        const value = getValue(p);
        p.barWidth = maxValue > 0 ? (value / maxValue) * 100 : 0;
        const ratio = practiceAvgValue > 0 ? value / practiceAvgValue : 1;
        p.color = ratio >= 1 ? '#16a34a' : ratio >= 0.9 ? '#ca8a04' : '#dc2626';
      });

      return { providers, practiceAvgValue, maxValue };
    },
  },

  methods: {
    ...mapActions('huddle', ['getProductionTrendsData']),

    async init() {
      await this.fetchData();
      this.setDefaultProvider();
    },

    async fetchData() {
      this.busy = true;
      try {
        const dateFrom = moment(this.selectedTimeFrame[0]).format('YYYY-MM-DD');
        const dateTo = moment(this.selectedTimeFrame[1]).format('YYYY-MM-DD');

        this.results = await this.getProductionTrendsData({
          siteID: this.siteID,
          dateFrom,
          dateTo,
        });
      } catch(err) {
        console.error('Error fetching production trends:', err);
        this.$toast.error('Failed to load production trends data.');
      } finally {
        this.busy = false;
      }
      // Re-validate selected providers against new results
      this.validateSelectedProviders();
      // Fetch comparison/Period B data in background (non-blocking)
      if (this.compareMode) {
        this.fetchPeriodBData();
      } else {
        this.fetchComparisonData();
      }
    },

    async fetchComparisonData() {
      const range = this.comparisonDateRange;
      if (!range) {
        this.comparisonResults = null;
        return;
      }
      try {
        this.comparisonResults = await this.getProductionTrendsData({
          siteID: this.siteID,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
        });
      } catch(err) {
        console.error('Error fetching comparison data:', err);
        this.comparisonResults = null;
      }
    },

    /**
     * Process raw API results into summary numbers (reuses _buildDailyData)
     */
    computeSummaryFromResults(results) {
      const daily = this._buildDailyData(results);
      if (!daily || Object.keys(daily).length === 0) return null;

      let totalNet = 0;
      let totalClinicalHours = 0;
      let workingDays = new Set();

      Object.keys(daily).forEach(dateStr => {
        this.selectedProvNums.forEach(provNum => {
          if (daily[dateStr] && daily[dateStr][provNum]) {
            const entry = daily[dateStr][provNum];
            totalNet += entry.netProduction;
            totalClinicalHours += entry.clinicalHours;
            if (entry.netProduction !== 0 || entry.clinicalHours > 0) {
              workingDays.add(dateStr);
            }
          }
        });
      });

      const avgPerHour = totalClinicalHours > 0 ? totalNet / totalClinicalHours : null;
      const dailyAvg = workingDays.size > 0 ? totalNet / workingDays.size : 0;

      return { totalNet, totalClinicalHours, avgPerHour, dailyAvg, workingDays: workingDays.size };
    },

    /**
     * Build daily per-provider data structure from raw API results
     * Reusable for both Period A (this.results) and Period B (this.periodBResults)
     */
    _buildDailyData(results) {
      if (!results || !results.data || results.data.length === 0) return {};

      const grossRaw = results.data[0] || [];
      const writeoffRaw = results.data[1] || [];
      const adjustmentsRaw = results.data[2] || [];
      const appointmentsRaw = results.data[3] || [];
      // Index 4 = Open Dental provider schedule. Absent on an older backend -> appointments-only hours.
      const scheduleRaw = Array.isArray(results.data[4]) ? results.data[4] : [];

      const data = {};

      grossRaw.forEach(row => {
        const dateStr = moment(row.ProcDate).format('YYYY-MM-DD');
        if (!data[dateStr]) data[dateStr] = {};
        if (!data[dateStr][row.ProvNum]) {
          data[dateStr][row.ProvNum] = { gross: 0, writeoffs: 0, adjustments: 0, clinicalHours: 0, providerName: row.Provider };
        }
        data[dateStr][row.ProvNum].gross += parseFloat(row.GrossAppointmentValue) || 0;
      });

      writeoffRaw.forEach(row => {
        const dateStr = moment(row.ProcDate).format('YYYY-MM-DD');
        if (!data[dateStr]) data[dateStr] = {};
        if (!data[dateStr][row.ProvNum]) {
          data[dateStr][row.ProvNum] = { gross: 0, writeoffs: 0, adjustments: 0, clinicalHours: 0, providerName: row.Provider };
        }
        data[dateStr][row.ProvNum].writeoffs += parseFloat(row.Writeoff) || 0;
      });

      adjustmentsRaw.forEach(row => {
        const dateStr = moment(row.AdjDate).format('YYYY-MM-DD');
        if (!data[dateStr]) data[dateStr] = {};
        if (!data[dateStr][row.ProvNum]) {
          data[dateStr][row.ProvNum] = { gross: 0, writeoffs: 0, adjustments: 0, clinicalHours: 0, providerName: row.Provider };
        }
        data[dateStr][row.ProvNum].adjustments += parseFloat(row.AdjAmt) || 0;
      });

      // Clinical hours per provider per day (rule: src/utils/huddle/clinicalHours.js). A hygiene visit is filed
      // under BOTH the dentist (ProvNum) and the hygienist (ProvHyg); the schedule blocks come from index 4.
      const aptsByDay = groupAppointmentsByProviderDay(
        appointmentsRaw,
        apt => moment(apt.AptDateTime).format('YYYY-MM-DD')
      );
      const schedByDay = groupScheduleByProviderDay(scheduleRaw);
      const providerLunchDuration = this.huddleGoals ? this.huddleGoals.providerLunchDuration : null;

      const dayKeys = new Set([...Object.keys(aptsByDay), ...Object.keys(schedByDay)]);
      dayKeys.forEach(dateStr => {
        const apptsForDay = aptsByDay[dateStr] || {};
        const blocksForDay = schedByDay[dateStr] || {};
        const provKeys = new Set([...Object.keys(apptsForDay), ...Object.keys(blocksForDay)]);
        const isoWeekday = moment(dateStr).isoWeekday();
        provKeys.forEach(provNum => {
          const blocks = blocksForDay[provNum] || [];
          const lunchMinutes = resolveLunchMinutes(providerLunchDuration, provNum, isoWeekday);
          const { hours, source } = computeDayHours({ appts: apptsForDay[provNum], blocks, lunchMinutes });
          // Scheduled but no appointments (holiday / stale template) -> 0 hours, nothing to record.
          if (source === 'none') return;
          if (!data[dateStr]) data[dateStr] = {};
          if (!data[dateStr][provNum]) {
            data[dateStr][provNum] = { gross: 0, writeoffs: 0, adjustments: 0, clinicalHours: 0, providerName: '' };
          }
          data[dateStr][provNum].clinicalHours = hours;
          data[dateStr][provNum].hoursSource = source;
        });
      });

      Object.keys(data).forEach(dateStr => {
        Object.keys(data[dateStr]).forEach(provNum => {
          const entry = data[dateStr][provNum];
          entry.netProduction = entry.gross - entry.writeoffs + entry.adjustments;
          entry.perHour = entry.clinicalHours > 0 ? entry.netProduction / entry.clinicalHours : null;
        });
      });

      return data;
    },

    /**
     * Aggregate daily data into monthly buckets (or return as-is for daily view)
     */
    _aggregateData(daily, isMonthly) {
      if (!daily || Object.keys(daily).length === 0) return {};
      if (!isMonthly) return daily;

      const monthly = {};
      Object.keys(daily).forEach(dateStr => {
        const monthKey = moment(dateStr).format('YYYY-MM');
        if (!monthly[monthKey]) monthly[monthKey] = {};

        Object.keys(daily[dateStr]).forEach(provNum => {
          const dayData = daily[dateStr][provNum];
          if (!monthly[monthKey][provNum]) {
            monthly[monthKey][provNum] = {
              gross: 0, writeoffs: 0, adjustments: 0, clinicalHours: 0,
              netProduction: 0, perHour: null, providerName: dayData.providerName
            };
          }
          monthly[monthKey][provNum].gross += dayData.gross;
          monthly[monthKey][provNum].writeoffs += dayData.writeoffs;
          monthly[monthKey][provNum].adjustments += dayData.adjustments;
          monthly[monthKey][provNum].clinicalHours += dayData.clinicalHours;
        });
      });

      Object.keys(monthly).forEach(monthKey => {
        Object.keys(monthly[monthKey]).forEach(provNum => {
          const entry = monthly[monthKey][provNum];
          entry.netProduction = entry.gross - entry.writeoffs + entry.adjustments;
          entry.perHour = entry.clinicalHours > 0 ? entry.netProduction / entry.clinicalHours : null;
        });
      });

      return monthly;
    },

    /**
     * Build chart data for comparison mode (Period A vs Period B with normalized X-axis)
     */
    _buildComparisonChartData() {
      const aggA = this.aggregatedData;
      const aggB = this.periodBAggregatedData;

      if ((!aggA || Object.keys(aggA).length === 0) && (!aggB || Object.keys(aggB).length === 0)) {
        return { labels: [], datasets: [] };
      }
      if (this.selectedProviders.length === 0) return { labels: [], datasets: [] };

      const keysA = Object.keys(aggA || {}).sort().filter(key =>
        this.selectedProvNums.some(pn => aggA[key] && aggA[key][pn])
      );
      const keysB = Object.keys(aggB || {}).sort().filter(key =>
        this.selectedProvNums.some(pn => aggB[key] && aggB[key][pn])
      );

      const maxLen = Math.max(keysA.length, keysB.length);
      if (maxLen === 0) return { labels: [], datasets: [] };

      // Labels: use month names for monthly view, normalized Day N for daily view
      const labels = [];
      if (this.isMonthlyView) {
        // Use actual month names from whichever period has more keys (they should align by month-of-year)
        const sourceKeys = keysA.length >= keysB.length ? keysA : keysB;
        for (let i = 0; i < maxLen; i++) {
          labels.push(sourceKeys[i] ? moment(sourceKeys[i], 'YYYY-MM').format('MMM') : 'Month ' + (i + 1));
        }
      } else {
        for (let i = 0; i < maxLen; i++) {
          labels.push('Day ' + (i + 1));
        }
      }

      const periodALabel = this._formatPeriodLabel(this.selectedTimeFrame);
      const periodBLabel = this._formatPeriodLabel(this.comparisonTimeFrame);

      const datasets = [];
      this.selectedProviders.forEach((prov, index) => {
        const provNum = String(prov.ProvNum);
        const color = PROVIDER_COLORS[index % PROVIDER_COLORS.length];

        // Period A (solid)
        const valuesA = [];
        for (let i = 0; i < maxLen; i++) {
          const key = keysA[i];
          if (key && aggA[key] && aggA[key][provNum]) {
            const entry = aggA[key][provNum];
            valuesA.push(this.yAxisMode === 'perHour'
              ? (entry.perHour !== null ? parseFloat(entry.perHour.toFixed(2)) : 0)
              : parseFloat(entry.netProduction.toFixed(2)));
          } else {
            valuesA.push(0);
          }
        }

        datasets.push({
          label: prov.Provider + ' (' + periodALabel + ')',
          data: valuesA,
          backgroundColor: color,
          borderWidth: 1,
        });

        // Period B (transparent)
        const valuesB = [];
        for (let i = 0; i < maxLen; i++) {
          const key = keysB[i];
          if (key && aggB[key] && aggB[key][provNum]) {
            const entry = aggB[key][provNum];
            valuesB.push(this.yAxisMode === 'perHour'
              ? (entry.perHour !== null ? parseFloat(entry.perHour.toFixed(2)) : 0)
              : parseFloat(entry.netProduction.toFixed(2)));
          } else {
            valuesB.push(0);
          }
        }

        datasets.push({
          label: prov.Provider + ' (' + periodBLabel + ')',
          data: valuesB,
          backgroundColor: color + '50',
          borderWidth: 1,
          borderColor: color,
        });
      });

      return { labels, datasets };
    },

    _formatPeriodLabel(timeFrame) {
      if (!timeFrame || !timeFrame[0] || !timeFrame[1]) return '';
      return moment(timeFrame[0]).format('MMM D') + ' \u2013 ' + moment(timeFrame[1]).format('MMM D, YYYY');
    },

    onBarHover(event, li, di, label, dsLabel, value) {
      const wrapper = this.$refs.chartWrapper;
      if (!wrapper) return;
      const wrapperRect = wrapper.getBoundingClientRect();
      const barRect = event.target.getBoundingClientRect();
      // Position tooltip centered above the bar, relative to the wrapper
      this.hoveredBar = {
        li,
        di,
        label,
        dsLabel,
        value,
        top: barRect.top - wrapperRect.top - 10,
        left: barRect.left - wrapperRect.left + (barRect.width / 2),
      };
    },

    formatBarValue(value) {
      if (value === null || value === undefined) return 'N/A';
      const formatted = this.currencySymbol + value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      return this.yAxisMode === 'perHour' ? formatted + '/hr' : formatted;
    },

    formatTickValue(value) {
      if (value >= 1000) {
        return this.currencySymbol + (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + 'k';
      }
      return this.currencySymbol + value.toLocaleString();
    },

    validateSelectedProviders() {
      if (this.availableProviders.length === 0) {
        this.selectedProviders = [];
        return;
      }
      // Keep only providers that exist in the new results
      const validProvNums = new Set(this.availableProviders.map(p => parseInt(p.ProvNum)));
      const valid = this.selectedProviders.filter(p => validProvNums.has(parseInt(p.ProvNum)));
      if (valid.length > 0) {
        this.selectedProviders = valid;
      } else {
        // No valid selection remains — fall back to default
        this.setDefaultProvider();
      }
    },

    setDefaultProvider() {
      if (this.availableProviders.length === 0) return;

      // Try to use UNSCHEDULED_TREATMENT_DEFAULT_PROVIDER setting (supports pipe-separated multi-value)
      if (this.allSettings?.UNSCHEDULED_TREATMENT_DEFAULT_PROVIDER?.PropertyValue) {
        const provNums = String(this.allSettings.UNSCHEDULED_TREATMENT_DEFAULT_PROVIDER.PropertyValue)
          .split('|').map(n => parseInt(n, 10)).filter(n => !isNaN(n) && n > 0);
        const defaults = _.uniqBy(this.availableProviders.filter(p => provNums.includes(parseInt(p.ProvNum))), 'ProvNum');
        if (defaults.length > 0) {
          this.selectedProviders = defaults;
          return;
        }
      }

      // Fallback: select first provider
      this.selectedProviders = [this.availableProviders[0]];
    },

    // Hover text for the detail-table hours cell: where the day's hours came from.
    hoursSourceLabel(source) {
      if (source === 'schedule') return 'From the Open Dental provider schedule, stretched to the first and last appointment if they fall outside it';
      if (source === 'appointments') return 'From first appointment to the end of the last one (no provider schedule that day)';
      return undefined;
    },

    providerLabel(provider) {
      return provider.Provider || 'Provider ' + provider.ProvNum;
    },

    setRange(range) {
      this.activePreset = range;
      switch(range) {
        case 'thisweek':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('isoWeek').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          break;
        case 'lastweek':
          Vue.set(this.selectedTimeFrame, 0, moment().subtract(1, 'weeks').startOf('isoWeek').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().subtract(1, 'weeks').endOf('isoWeek').toDate());
          break;
        case 'thismonth':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('month').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          break;
        case 'lastmonth':
          Vue.set(this.selectedTimeFrame, 0, moment().subtract(1, 'months').startOf('month').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().subtract(1, 'months').endOf('month').toDate());
          break;
        case 'thisyear':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('year').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          break;
        case 'lastyear':
          Vue.set(this.selectedTimeFrame, 0, moment().subtract(1, 'years').startOf('year').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().subtract(1, 'years').endOf('year').toDate());
          break;
      }
      this.fetchData();
    },

    onDateChange() {
      this.activePreset = null;
      if (this.selectedTimeFrame && this.selectedTimeFrame[0] && this.selectedTimeFrame[1]) {
        this.fetchData();
      }
    },

    sortTable(key) {
      if (this.sortBy === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = key;
        this.sortDirection = 'asc';
      }
    },

    toggleCompareMode() {
      this.compareMode = !this.compareMode;
    },

    setComparisonPreset(preset) {
      this.comparisonPreset = preset;
      switch(preset) {
        case 'monthVsMonth':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('month').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          this.activePreset = 'thismonth';
          Vue.set(this.comparisonTimeFrame, 0, moment().subtract(1, 'months').startOf('month').toDate());
          Vue.set(this.comparisonTimeFrame, 1, moment().subtract(1, 'months').endOf('month').toDate());
          break;
        case 'weekVsWeek':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('isoWeek').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          this.activePreset = 'thisweek';
          Vue.set(this.comparisonTimeFrame, 0, moment().subtract(1, 'weeks').startOf('isoWeek').toDate());
          Vue.set(this.comparisonTimeFrame, 1, moment().subtract(1, 'weeks').endOf('isoWeek').toDate());
          break;
        case 'yearVsYear':
          Vue.set(this.selectedTimeFrame, 0, moment().startOf('year').toDate());
          Vue.set(this.selectedTimeFrame, 1, moment().toDate());
          this.activePreset = 'thisyear';
          Vue.set(this.comparisonTimeFrame, 0, moment().subtract(1, 'years').startOf('year').toDate());
          Vue.set(this.comparisonTimeFrame, 1, moment().subtract(1, 'years').endOf('year').toDate());
          break;
      }
      this.fetchData();
    },

    async fetchPeriodBData() {
      if (!this.comparisonTimeFrame || !this.comparisonTimeFrame[0] || !this.comparisonTimeFrame[1]) {
        this.periodBResults = null;
        return;
      }
      try {
        this.periodBResults = await this.getProductionTrendsData({
          siteID: this.siteID,
          dateFrom: moment(this.comparisonTimeFrame[0]).format('YYYY-MM-DD'),
          dateTo: moment(this.comparisonTimeFrame[1]).format('YYYY-MM-DD'),
        });
      } catch(err) {
        console.error('Error fetching Period B data:', err);
        this.periodBResults = null;
      }
    },

    onPeriodBDateChange() {
      this.comparisonPreset = null;
      if (this.comparisonTimeFrame && this.comparisonTimeFrame[0] && this.comparisonTimeFrame[1]) {
        this.fetchPeriodBData();
      }
    },

    async closeSlideOutPanel() {
      this.$emit('closePanel', { closed: true });
    },
  },
};
</script>

<style src="./HuddleComponentsModern.scss" lang="scss" />

<style scoped>
.timeframe-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.time-btn {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #475569;
  background: #f1f5f9;
  text-decoration: none;
  transition: all 0.15s;
  white-space: nowrap;
}

.time-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
  text-decoration: none;
}

.time-btn.active {
  background: #3abf94;
  color: #fff;
}

/* --- CSS Bar Chart --- */
.css-chart-wrapper {
  position: relative;
}

.css-chart-title {
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  padding-bottom: 12px;
}

.css-chart {
  display: flex;
  height: 298px;
  position: relative;
  overflow-x: auto;
}

/* Y-Axis: flex column matching plot + xlabel spacer */
.css-chart-yaxis {
  width: 55px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
}

.css-chart-yaxis-plot {
  flex: 1;
  position: relative;
}

.css-chart-yaxis-xlabel-spacer {
  height: 28px;
  flex-shrink: 0;
}

.css-chart-ytick {
  position: absolute;
  right: 8px;
  transform: translateY(50%);
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
}

/* Chart content: flex column with plot area + x-labels */
.css-chart-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* Plot area: gridlines + bars share this exact container */
.css-chart-plot {
  flex: 1;
  position: relative;
}

.css-chart-gridline {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #f1f5f9;
  pointer-events: none;
}

/* Bar groups fill the plot area exactly */
.css-chart-groups {
  display: flex;
  align-items: flex-end;
  height: 100%;
  padding: 0 8px;
  gap: 2px;
  position: relative;
  z-index: 1;
}

.css-chart-group {
  flex: 1;
  min-width: 32px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 100%;
  gap: 2px;
  padding: 0 4px;
}

.css-chart-bar {
  flex: 1;
  max-width: 40px;
  min-width: 6px;
  border-radius: 3px 3px 0 0;
  transition: height 0.25s ease, opacity 0.15s ease;
  position: relative;
  cursor: pointer;
}

.css-chart-bar:hover {
  opacity: 0.85;
}

.css-chart-bar-tooltip {
  position: absolute;
  transform: translate(-50%, -100%);
  background: #1e293b;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.18);
}

.css-chart-bar-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1e293b;
}

/* X-Axis labels: separate row below plot area */
.css-chart-xlabels {
  display: flex;
  height: 28px;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 2px;
}

.css-chart-xlabel {
  flex: 1;
  min-width: 32px;
  font-size: 11px;
  color: #64748b;
  text-align: center;
  padding-top: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.css-chart-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  padding-top: 12px;
}

.css-chart-legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #475569;
}

.css-chart-legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 6px;
  flex-shrink: 0;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.12s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.trend-indicator {
  font-size: 11px;
  font-weight: 500;
}

.trend-up {
  color: #16a34a;
}

.trend-down {
  color: #dc2626;
}

.trend-neutral {
  color: #94a3b8;
}

.ranking-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f5f9;
}

.ranking-row:last-child {
  border-bottom: none;
}

.ranking-rank {
  width: 40px;
  font-weight: 600;
  color: #64748b;
  flex-shrink: 0;
}

.ranking-info {
  flex: 1;
  min-width: 0;
}

.ranking-name {
  font-size: 13px;
  margin-bottom: 4px;
}

.ranking-bar-wrapper {
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  position: relative;
  overflow: visible;
}

.ranking-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.ranking-avg-line {
  position: absolute;
  top: -4px;
  bottom: -4px;
  width: 2px;
  background: #94a3b8;
}

.ranking-avg-row {
  border-top: 2px dashed #e2e8f0;
  margin-top: 4px;
  padding-top: 12px;
}

.ranking-values {
  width: 120px;
  text-align: right;
  flex-shrink: 0;
  padding-left: 12px;
}

.compare-active-label {
  color: #ca8a04;
  font-weight: 600;
}

</style>
